import React, { useState } from 'react';
import { useMutation } from 'hooks/apollo';

import { FormattedMessage, useIntl } from 'react-intl';
import { Form, Radio, Input, Row, Col, Divider, InputNumber, Select, Slider, Checkbox } from 'antd';
import Icon, { CommentOutlined, MessageOutlined, SnippetsOutlined, FileTextOutlined } from '@ant-design/icons';
import SurveyGroupSelect from 'components/SurveyGroupSelect';
import FormLabel from 'components/FormLabel';

import useGlobalContext from 'hooks/useGlobalContext';
import PopupPreview from './ContainerPreviews/Popup';
import ModalPreview from './ContainerPreviews/Modal';
import Bubble from './ContainerPreviews/Bubble';
import Embed from './ContainerPreviews/Embed';
import NewTab from './ContainerPreviews/NewWindow';

import Timer from './TriggerPreviews/Timer';
import ExistingButton from './TriggerPreviews/ExistingButton';
import FloatingBottomRight from './TriggerPreviews/ButtonBottomRight';
import FloatingBottomLeft from './TriggerPreviews/ButtonBottomLeft';
import FloatingCenterRight from './TriggerPreviews/ButtonCenterRight';
import FixedBottomRight from './TriggerPreviews/TabBottomRight';
import FixedBottomLeft from './TriggerPreviews/TabBottomLeft';
import FixedCenterRight from './TriggerPreviews/TabCenterRight';

import messages from './messages';
import mutation from './updateStyle.gql';

const TRIGGERS = {
  NONE: 'none',
  TIMER: 'invisibleLayer',
  BUTTON: 'button',
  EXISTING_BUTTON: 'existingButton',
};

const TRIGGER_POSITIONS = {
  CENTER_RIGHT: 'centerRight',
  BOTTOM_LEFT: 'bottomLeft',
  BOTTOM_RIGHT: 'bottomRight',
};

const BUTTON_STYLES = {
  FLOATING: 'floatingButton',
  FIXED: 'tab',
};

const BUTTON_ICONS = {
  COMMENT: 'comment',
  COMMENTS: 'comments',
  CLIPBOARD: 'clipboard',
  LIST: 'list-alt',
};

// Containers of type `popupLayer` also
// have a container type `modal || speechBubble`
// So we need to deal with that.
const CONTAINERS = {
  POPUP: 'popupWindow',
  EMBED: 'embed',
  NEW_TAB: 'newWindow',
  POPUP_LAYER: 'popupLayer', // DONT USE. se comment.
  MODAL: 'modal',
  BUBBLE: 'speechBubble',
};

const DEFAULT_TRIGGERS = {
  [CONTAINERS.POPUP]: TRIGGERS.TIMER,
  [CONTAINERS.EMBED]: TRIGGERS.NONE,
  [CONTAINERS.NEW_TAB]: TRIGGERS.TIMER,
  [CONTAINERS.MODAL]: TRIGGERS.TIMER,
  [CONTAINERS.BUBBLE]: TRIGGERS.BUTTON,
};

const toMin = (val) => {
  if (!val) { return null; }
  const v = parseInt(val, 10);
  return v / 60;
};

const fromMin = val => val && val * 60;

const toDays = (val) => {
  if (!val) { return null; }
  const v = parseInt(val, 10);
  return v / (60 * 60 * 24);
};

const fromDays = val => val && val * 60 * 60 * 24;

const EditDistributionStyle = ({ customerId, distribution, form, onDone }) => {
  const { formatMessage: $t } = useIntl();
  const { message } = useGlobalContext();

  const containerType = distribution.container === CONTAINERS.POPUP_LAYER
    ? (distribution.containerType || CONTAINERS.MODAL)
    : distribution.container;

  const initialValues = {
    trigger: distribution.trigger || DEFAULT_TRIGGERS[containerType || CONTAINERS.POPUP],
    delay: toMin(distribution.delay) || 2,
    sessionDelay: toMin(distribution.sessionDelay) || 1,
    frequency: toDays(distribution.frequency) || 90,
    declineFrequency: toDays(distribution.declineFrequency) || 30,
    sharedSurveys: distribution.groupSurveys.map((s) => s.id),
    triggerStyle: distribution.triggerStyle || BUTTON_STYLES.FLOATING,
    triggerPosition: distribution.triggerPosition || TRIGGER_POSITIONS.BOTTOM_RIGHT,
    container: containerType || CONTAINERS.POPUP,
    popupWidth: distribution.popupWidth || 520,
    popupHeight: distribution.popupHeight || 450,
    buttonIcon: distribution.buttonIcon || BUTTON_ICONS.COMMENT,
    buttonLabel: distribution.buttonLabel,
    elementSelector: distribution.elementSelector,
    quizMode: distribution.quizMode,
  };

  const [container, setContainer] = useState(initialValues.container);
  const [trigger, setTrigger] = useState(initialValues.trigger);
  const [triggerPosition, setTriggerPosition] = useState(initialValues.triggerPosition);
  const [triggerStyle, setButtonStyle] = useState(initialValues.triggerStyle);

  const onTriggerChange = (e) => setTrigger(e.target.value);
  const onTriggerStyleChange = (e) => setButtonStyle(e.target.value);
  const onTriggerPositionChange = (e) => setTriggerPosition(e.target.value);
  const onContainerChange = (value) => {
    setContainer(value);
    if (!distribution.trigger) {
      setTrigger(DEFAULT_TRIGGERS[value]);
      form.setFieldsValue({ trigger: DEFAULT_TRIGGERS[value] });
    }
  };
  const minuteFormatter = (value) => {
    const min = `${Math.floor(value)} min`;
    const sec = (value % 1) * 60;
    return sec ? `${min} ${sec} sec` : min;
  };

  const [save] = useMutation(mutation);
  const onFinish = async (values) => {
    const containerValue = container === CONTAINERS.MODAL || values.container === CONTAINERS.BUBBLE
      ? CONTAINERS.POPUP_LAYER
      : values.container;
    const containerTypeValue = containerValue === CONTAINERS.POPUP_LAYER ? values.container : null;

    const styleInput = {
      ...values,
      delay: fromMin(values.delay)?.toString(),
      sessionDelay: fromMin(values.sessionDelay),
      frequency: fromDays(values.frequency)?.toString(),
      declineFrequency: fromDays(values.declineFrequency),
      container: containerValue,
      containerType: containerTypeValue,
    };

    try {
      await save({ variables: { id: distribution.id, style: styleInput } });
      message.success($t(messages.saveStyleSuccess));
    } catch (error) {
      message.error($t(messages.saveStyleError));
    }
    if (onDone) {
      onDone();
    }
  };

  return (
    <Form layout="vertical" form={form} onFinish={onFinish} initialValues={initialValues}>
      <Row gutter={16}>
        <Col sm={24} md={16}>
          <Form.Item name="container" label={<FormattedMessage {...messages.container} />}>
            <Select onChange={onContainerChange}>
              <Select.Option key={CONTAINERS.POPUP} value={CONTAINERS.POPUP}>
                <FormattedMessage {...messages.containerPopupWindow} />
              </Select.Option>
              <Select.Option key={CONTAINERS.MODAL} value={CONTAINERS.MODAL}>
                <FormattedMessage {...messages.containerPopupModal} />
              </Select.Option>
              <Select.Option key={CONTAINERS.BUBBLE} value={CONTAINERS.BUBBLE}>
                <FormattedMessage {...messages.containerSpeechBubble} />
              </Select.Option>
              <Select.Option key={CONTAINERS.EMBED} value={CONTAINERS.EMBED}>
                <FormattedMessage {...messages.containerEmbed} />
              </Select.Option>
              <Select.Option key={CONTAINERS.NEW_TAB} value={CONTAINERS.NEW_TAB}>
                <FormattedMessage {...messages.containerNewWindow} />
              </Select.Option>
            </Select>
          </Form.Item>
          {(container === CONTAINERS.POPUP || container === CONTAINERS.MODAL) && (
            <Row gutter={16}>
              <Col sm={24} md={12}>
                <Form.Item name="popupWidth" label={<FormattedMessage {...messages.popupWidth} />}>
                  <InputNumber
                    style={{ width: '100%' }}
                    formatter={value => `${value}px`}
                    parser={value => value.replace('px', '')}
                  />
                </Form.Item>
              </Col>
              <Col sm={24} md={12}>
                <Form.Item name="popupHeight" label={<FormattedMessage {...messages.popupHeight} />}>
                  <InputNumber
                    style={{ width: '100%' }}
                    formatter={value => `${value}px`}
                    parser={value => value.replace('px', '')}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}
          {container === CONTAINERS.EMBED && (
            <Form.Item name="elementSelector" label={<FormattedMessage {...messages.containerSelector} />}>
              <Input placeholder={$t(messages.containerSelectorPlaceholder)} />
            </Form.Item>
          )}
          <Form.Item
            name="quizMode"
            valuePropName="checked"
            label={<FormLabel label={messages.quizMode} description={messages.quizModeTooltip} tooltipPlacement="topRight" />}
          >
            <Checkbox>
              <FormattedMessage {...messages.quizModeLabel} />
            </Checkbox>
          </Form.Item>
        </Col>
        <Col sm={24} md={8} style={{ display: 'flex', justifyContent: 'center', paddingBottom: 16 }}>
          {container === CONTAINERS.POPUP && <Icon component={PopupPreview} />}
          {container === CONTAINERS.MODAL && <Icon component={ModalPreview} />}
          {container === CONTAINERS.BUBBLE && <Icon component={Bubble} />}
          {container === CONTAINERS.EMBED && <Icon component={Embed} />}
          {container === CONTAINERS.NEW_TAB && <Icon component={NewTab} />}
        </Col>
      </Row>
      <Divider />
      <Row gutter={16}>
        <Col sm={24} md={16}>
          <Form.Item name="trigger" label={<FormattedMessage {...messages.trigger} />}>
            <Radio.Group buttonStyle="solid" onChange={onTriggerChange}>
              <Radio.Button value={TRIGGERS.TIMER}><FormattedMessage {...messages.triggerTimer} /></Radio.Button>
              <Radio.Button value={TRIGGERS.BUTTON}><FormattedMessage {...messages.triggerButton} /></Radio.Button>
              <Radio.Button value={TRIGGERS.EXISTING_BUTTON}><FormattedMessage {...messages.triggerExistingButton} /></Radio.Button>
              <Radio.Button value={TRIGGERS.NONE}><FormattedMessage {...messages.triggerNone} /></Radio.Button>
            </Radio.Group>
          </Form.Item>

          {trigger === TRIGGERS.NONE && (
            <p><FormattedMessage {...messages.noTriggerDescription} /></p>
          )}

          {trigger === TRIGGERS.TIMER && (
            <>
              <Form.Item name="delay" label={<FormattedMessage {...messages.delay} />}>
                <Slider
                  min={0}
                  max={10}
                  step={0.25}
                  tooltip={{
                    formatter: minuteFormatter,
                  }}
                  marks={{ 0.5: '30 sec', 2: '2 min', 5: '5 min', 10: '10 min' }}
                />
              </Form.Item>
              <Form.Item name="sessionDelay" label={<FormattedMessage {...messages.sessionDelay} />}>
                <Slider
                  min={0}
                  max={5}
                  step={0.25}
                  tooltip={{
                    formatter: minuteFormatter,
                  }}
                  marks={{ 0.5: '30 sec', 1: '1 min', 5: '5 min' }}
                />
              </Form.Item>
              <Form.Item name="declineFrequency" label={<FormattedMessage {...messages.declineFrequency} />}>
                <Slider
                  min={0}
                  max={90}
                  tooltip={{
                    formatter: (v) => `${v} days`,
                  }}
                  marks={{ 14: '2 weeks', 30: '1 month', 60: '2 months', 90: '3 months' }}
                />
              </Form.Item>
              <Form.Item name="frequency" label={<FormattedMessage {...messages.frequency} />}>
                <Slider
                  min={0}
                  max={365}
                  tooltip={{
                    formatter: (v) => `${v} days`,
                  }}
                  marks={{ 30: '1 month', 90: '3 months', 180: '6 months', 365: '1 year' }}
                />
              </Form.Item>
              <Form.Item name="sharedSurveys" label={<FormattedMessage {...messages.sharedSurveys} />}>
                <SurveyGroupSelect customerId={customerId} treeCheckable />
              </Form.Item>
            </>
          )}

          {trigger === TRIGGERS.EXISTING_BUTTON && (
            <Form.Item name="elementSelector" label={<FormattedMessage {...messages.buttonSelector} />}>
              <Input placeholder={$t(messages.buttonSelectorPlaceholder)} />
            </Form.Item>
          )}

          {trigger === TRIGGERS.BUTTON && (
            <>
              <Form.Item name="triggerPosition" label={<FormattedMessage {...messages.triggerPosition} />}>
                <Radio.Group buttonStyle="solid" onChange={onTriggerPositionChange}>
                  <Radio.Button value={TRIGGER_POSITIONS.BOTTOM_RIGHT}><FormattedMessage {...messages.bottomRight} /></Radio.Button>
                  <Radio.Button value={TRIGGER_POSITIONS.BOTTOM_LEFT}><FormattedMessage {...messages.bottomLeft} /></Radio.Button>
                  <Radio.Button value={TRIGGER_POSITIONS.CENTER_RIGHT}><FormattedMessage {...messages.centerRight} /></Radio.Button>
                </Radio.Group>
              </Form.Item>

              <Form.Item name="triggerStyle" label={<FormattedMessage {...messages.triggerStyle} />}>
                <Radio.Group buttonStyle="solid" onChange={onTriggerStyleChange}>
                  <Radio.Button value={BUTTON_STYLES.FLOATING}><FormattedMessage {...messages.floating} /></Radio.Button>
                  <Radio.Button value={BUTTON_STYLES.FIXED}><FormattedMessage {...messages.fixed} /></Radio.Button>
                </Radio.Group>
              </Form.Item>

              {triggerStyle === BUTTON_STYLES.FLOATING && (
                <Form.Item name="buttonIcon" label={<FormattedMessage {...messages.buttonIcon} />}>
                  <Radio.Group buttonStyle="solid">
                    <Radio.Button value={BUTTON_ICONS.COMMENT}><MessageOutlined /></Radio.Button>
                    <Radio.Button value={BUTTON_ICONS.COMMENTS}><CommentOutlined /></Radio.Button>
                    <Radio.Button value={BUTTON_ICONS.CLIPBOARD}><SnippetsOutlined /></Radio.Button>
                    <Radio.Button value={BUTTON_ICONS.LIST}><FileTextOutlined /></Radio.Button>
                  </Radio.Group>
                </Form.Item>
              )}

              {triggerStyle === BUTTON_STYLES.FIXED && (
                <Form.Item name="buttonLabel" label={<FormattedMessage {...messages.buttonLabel} />}>
                  <Input />
                </Form.Item>
              )}
            </>
          )}
        </Col>
        <Col sm={24} md={8} style={{ display: 'flex', justifyContent: 'center', paddingBottom: 16 }}>
          {trigger === TRIGGERS.TIMER && <Icon component={Timer} />}
          {trigger === TRIGGERS.EXISTING_BUTTON && <Icon component={ExistingButton} />}
          {trigger === TRIGGERS.BUTTON
            && triggerStyle === BUTTON_STYLES.FLOATING
            && triggerPosition === TRIGGER_POSITIONS.BOTTOM_RIGHT
            && <Icon component={FloatingBottomRight} />}
          {trigger === TRIGGERS.BUTTON
            && triggerStyle === BUTTON_STYLES.FLOATING
            && triggerPosition === TRIGGER_POSITIONS.BOTTOM_LEFT
            && <Icon component={FloatingBottomLeft} />}
          {trigger === TRIGGERS.BUTTON
            && triggerStyle === BUTTON_STYLES.FLOATING
            && triggerPosition === TRIGGER_POSITIONS.CENTER_RIGHT
            && <Icon component={FloatingCenterRight} />}
          {trigger === TRIGGERS.BUTTON
            && triggerStyle === BUTTON_STYLES.FIXED
            && triggerPosition === TRIGGER_POSITIONS.BOTTOM_RIGHT
            && <Icon component={FixedBottomRight} />}
          {trigger === TRIGGERS.BUTTON
            && triggerStyle === BUTTON_STYLES.FIXED
            && triggerPosition === TRIGGER_POSITIONS.BOTTOM_LEFT
            && <Icon component={FixedBottomLeft} />}
          {trigger === TRIGGERS.BUTTON
            && triggerStyle === BUTTON_STYLES.FIXED
            && triggerPosition === TRIGGER_POSITIONS.CENTER_RIGHT
            && <Icon component={FixedCenterRight} />}
        </Col>
      </Row>
    </Form>
  );
};

export default EditDistributionStyle;
